// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import { getObjectKeys, NullPartial, omitKeys, pickKeys, subsetTypeHasKey, ValueOf } from "@hex-insights/core";
import {
	MediaAttachmentCreateInput,
	MediaAttachmentDetailQuery,
	MediaAttachmentInAnnouncementCreateInput,
	MediaAttachmentInCalendarEventCreateInput,
	MediaAttachmentInMessageCreateInput,
	MediaAttachmentInPostCreateInput,
	MediaAttachmentUpdateInput,
} from "../../../Utilities";
import * as MediaAttachmentFormValues from "./form-values";

/**
 * Converts the given `formValues` for the create form to a GraphQL create input for the MediaAttachment model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInput<
	K extends keyof MediaAttachmentFormValues.Create = keyof MediaAttachmentFormValues.Create,
>(formValues: MediaAttachmentFormValues.CreateSubset<K>): MediaAttachmentCreateInput {
	const inputFormValues = omitKeys(formValues, ["isFile"]);
	const input: NullPartial<MediaAttachmentCreateInput> = { ...inputFormValues };
	return input as MediaAttachmentCreateInput;
}

/**
 * Converts the given `formValues` for the create form nested in the Announcement to a GraphQL nested create input for the MediaAttachment model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInAnnouncementInput<
	K extends keyof MediaAttachmentFormValues.CreateInAnnouncement = keyof MediaAttachmentFormValues.CreateInAnnouncement,
>(formValues: MediaAttachmentFormValues.CreateInAnnouncementSubset<K>): MediaAttachmentInAnnouncementCreateInput {
	const inputFormValues = omitKeys(formValues, ["isFile"]);
	const input: NullPartial<MediaAttachmentInAnnouncementCreateInput> = { ...inputFormValues };
	return input as MediaAttachmentInAnnouncementCreateInput;
}

/**
 * Converts the given `formValues` for the create form nested in the CalendarEvent to a GraphQL nested create input for the MediaAttachment model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInCalendarEventInput<
	K extends keyof MediaAttachmentFormValues.CreateInCalendarEvent = keyof MediaAttachmentFormValues.CreateInCalendarEvent,
>(formValues: MediaAttachmentFormValues.CreateInCalendarEventSubset<K>): MediaAttachmentInCalendarEventCreateInput {
	const inputFormValues = omitKeys(formValues, ["isFile"]);
	const input: NullPartial<MediaAttachmentInCalendarEventCreateInput> = { ...inputFormValues };
	return input as MediaAttachmentInCalendarEventCreateInput;
}

/**
 * Converts the given `formValues` for the create form nested in the Message to a GraphQL nested create input for the MediaAttachment model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInMessageInput<
	K extends keyof MediaAttachmentFormValues.CreateInMessage = keyof MediaAttachmentFormValues.CreateInMessage,
>(formValues: MediaAttachmentFormValues.CreateInMessageSubset<K>): MediaAttachmentInMessageCreateInput {
	const inputFormValues = omitKeys(formValues, ["isFile"]);
	const input: NullPartial<MediaAttachmentInMessageCreateInput> = { ...inputFormValues };
	return input as MediaAttachmentInMessageCreateInput;
}

/**
 * Converts the given `formValues` for the create form nested in the Post to a GraphQL nested create input for the MediaAttachment model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInPostInput<
	K extends keyof MediaAttachmentFormValues.CreateInPost = keyof MediaAttachmentFormValues.CreateInPost,
>(formValues: MediaAttachmentFormValues.CreateInPostSubset<K>): MediaAttachmentInPostCreateInput {
	const inputFormValues = omitKeys(formValues, ["isFile"]);
	const input: NullPartial<MediaAttachmentInPostCreateInput> = { ...inputFormValues };
	return input as MediaAttachmentInPostCreateInput;
}

/**
 * Converts the given `formValues` for the detail form to a GraphQL update input for the MediaAttachment model.
 *
 * @param formValues The form values to convert. Generally only the changed form values.
 * @param initialFormValues The initial values of the form.
 */
export function toGQLUpdateInput<
	K extends keyof MediaAttachmentFormValues.Detail = keyof MediaAttachmentFormValues.Detail,
>(
	formValues: Partial<MediaAttachmentFormValues.DetailSubset<K>>,
	_initialFormValues?: MediaAttachmentFormValues.DetailSubset<K>,
): MediaAttachmentUpdateInput {
	const inputFormValues = omitKeys(formValues, ["isFile", "announcementID", "calendarEventID", "messageID", "postID"]);
	const input: MediaAttachmentUpdateInput = { ...inputFormValues };
	return input;
}

export type MediaAttachmentFormKeyMap = {
	displayOrder: "displayOrder";
	title: "title";
	description: "description";
	isFile: "isFile";
	fileKey: "fileKey";
	fileContentType: "fileContentType";
	linkURL: "linkURL";
	announcementID: "announcement";
	calendarEventID: "calendarEvent";
	messageID: "message";
	postID: "post";
};

export type ToFormValuesArgs<
	K extends keyof MediaAttachmentFormValues.Detail = keyof MediaAttachmentFormValues.Detail,
> = {
	mediaAttachment: Pick<MediaAttachmentDetailQuery["mediaAttachment"], ValueOf<Pick<MediaAttachmentFormKeyMap, K>>>;
	fields?: { [Key in K]: true };
};

/**
 * Converts a MediaAttachment instance to its corresponding detail form values.
 *
 * @param mediaAttachment The instance to convert.
 * @param fields The subset of fields to include.
 */
export function toFormValues<
	K extends keyof MediaAttachmentFormValues.Detail = keyof MediaAttachmentFormValues.Detail,
>({ mediaAttachment, fields }: ToFormValuesArgs<K>): MediaAttachmentFormValues.DetailSubset<K> {
	const formValues = {
		displayOrder: subsetTypeHasKey(mediaAttachment, "displayOrder") ? mediaAttachment.displayOrder : null,
		title: subsetTypeHasKey(mediaAttachment, "title") ? mediaAttachment.title : "",
		description: subsetTypeHasKey(mediaAttachment, "description") ? mediaAttachment.description : "",
		isFile: subsetTypeHasKey(mediaAttachment, "isFile") ? mediaAttachment.isFile : false,
		fileKey: subsetTypeHasKey(mediaAttachment, "fileKey") ? mediaAttachment.fileKey : "",
		fileContentType: subsetTypeHasKey(mediaAttachment, "fileContentType") ? mediaAttachment.fileContentType : "",
		linkURL: subsetTypeHasKey(mediaAttachment, "linkURL") ? mediaAttachment.linkURL : "",
		announcementID: subsetTypeHasKey(mediaAttachment, "announcement") ? mediaAttachment.announcement?.id ?? null : null,
		calendarEventID: subsetTypeHasKey(mediaAttachment, "calendarEvent")
			? mediaAttachment.calendarEvent?.id ?? null
			: null,
		messageID: subsetTypeHasKey(mediaAttachment, "message") ? mediaAttachment.message?.id ?? null : null,
		postID: subsetTypeHasKey(mediaAttachment, "post") ? mediaAttachment.post?.id ?? null : null,
	};
	return fields ? pickKeys(formValues, getObjectKeys(fields)) : formValues;
}
